import { CampaignDetails, CurrentBrand } from 'modules/Types';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import useBrand from './useBrand';
import { getSymphonyPlatformMetrics_api } from 'api/brandApi';
import { DATE_OPTIONS } from 'pages/DashboardPage/utils';
import { getFanbaseData_api, PlatformDataResponse } from 'api/dashboardApi';
import { FanbaseMetricsType } from 'pages/DashboardPage/DashboardPage';
import dayjs from 'dayjs';
import { CampaignStatus, SocialConnectType } from 'modules/Const';
import { CAMPAIGN_FILTER_DEFAULT } from 'pages/MarketingPage/constants';
import { getCampaignsByBrandId_api } from 'api/campaignApi';
/** Data point structure for the graph */
export interface GraphDataPoint {
    /** Date string for the x-axis */
    date: string
    /** Numeric value for the y-axis */
    value: number
}

interface OverallGraphData {
    /** Array of data points with dates and summed values */
    chartData: GraphDataPoint[];
    /** Overall growth percentage across the time period */
    percentage: number;
    /** Array of date strings for x-axis labels */
    labels: string[];
}


/** Type for platform configuration data */
export type DashboardDataType = {
    /** Key for the platform */
    key: string;
    /** Display name for the platform */
    name: string;
    /** Platform identifier */
    platformId: string;
    /** Platform name shown in UI */
    platformName: string;
    /** Type of stat to track (followers, monthly_listeners, etc) */
    statIdentifier: string;
    /** Label for the stat being tracked */
    statLabel: string;
    /** SVG icon asset path */
    icon: string;
    /** Function to check if platform needs connection */
    needsConnection: (currentBrand: CurrentBrand) => boolean;
    /** Optional message if platform is decommissioned */
    decommissionMessage?: string;
    /** Whether to hide data from overall stats */
    hideDataFromOverall?: boolean;
    /** Platform type identifier */
    type: 'platforms' | 'symphony';
    /** Whether to hide from overall */
    hideFromOverall?: boolean;
    /** Name of the platform in the brand.connections object */
    getUsername?: (currentBrand: CurrentBrand) => string | null;
}

export interface GraphPresentation {
    /** Platform identifier */
    key: string;
    /** Display name for the platform */
    platformId: string;
    /** Display name for the platform */
    platformName: string;
    /** Label for the stat being tracked */
    statName: string;
    /** Stat identifier */
    statIdentifier: string;
    /** Whether platform is connected */
    isConnected: boolean;
    /** Array of data points for the graph */
    chartData: GraphDataPoint[];
    /** Path to platform icon */
    imageAsset: string;
    /** Current total value */
    currentValue: number;
    /** Whether to show in UI */
    shown?: boolean;
    /** Growth percentage */
    changeValue: number;
    /** Message if platform is decommissioned */
    decommissionMessage?: string;
}

/** Interface for chart data point */
interface ChartDataPoint {
    /** ISO date string */
    date: string;
    /** Numeric value for the data point */
    value: number;
}

/** Interface for raw chart data from API */
interface RawChartData {
    chartData: Array<{
        datetime: string;
        fanbase_count: string | number;
    }>;
    growthData: {
        currentGrowthValue: number;
        percentage: number;
    };
}

/** Interface for processed chart data */
interface ProcessedChartData {
    chartData: ChartDataPoint[];
    growthData: {
        currentGrowthValue: number;
        percentage: number;
    };
}

interface DashboardState {
    // State
    campaignsRecent: CampaignDetails[]
    isLoadingCampaignsRecent: boolean;
    loadedDataForBrandId: number | null;
    isLoadingInitialData: boolean;
    isLoadingNewDate: boolean;
    hasLoadedInitialData: boolean;

    // Date Options
    timeRange: DATE_OPTIONS;
    selectedDate: {
        startDate: string;
        endDate: string;
    };

    selectedPlatformForConnect: SocialConnectType | null;

    platformGraphsData: GraphPresentation[];
    overallGraphData: OverallGraphData | null;
    dashboardTrackedPlatforms: Record<string, DashboardDataType>;

    // Actions
    setCampaignsRecent: (campaignsRecent: CampaignDetails[]) => void;
    setIsLoadingCampaignsRecent: (isLoadingCampaignsRecent: boolean) => void;
    isPlatformConnected: (key: DashboardDataType['key']) => boolean;
    setSelectedDate: (selectedDate: { startDate: string; endDate: string }) => void;
    setTimeRange: (timeRange: DATE_OPTIONS) => void;
    setSelectedPlatformForConnect: (showConnectBox: SocialConnectType | null) => void;

    // Methods
    refreshDashboardData: () => void;
    refreshConnections: () => void;
    updateBrandConnections: () => void;
    getDashboardData: ({
        forceRefresh,
        startDate,
        endDate
    }: {
        forceRefresh?: boolean;
        startDate?: string;
        endDate?: string;
    }) => Promise<{ fanbaseMetrics: any, platformData: PlatformDataResponse[] }>;
    getDataForSpecificPlatform: (params: {
        /** Platform identifier */
        platformId: string;
        /** Stat identifier (e.g., 'followers', 'monthly_listeners') */
        statIdentifier: string;
        /** Start date in YYYY-MM-DD format */
        startDate: string;
        /** End date in YYYY-MM-DD format */
        endDate: string;
    }) => Promise<{
        chartData: GraphDataPoint[];
        percentage: number;
    }>;
    getCampaignsRecent: ({
        brandId,
        isAnalytics
    }: {
        brandId: number,
        isAnalytics?: boolean
    }) => Promise<CampaignDetails[]>;
}

export const useDashboard = create(immer<DashboardState>((set, get) => ({
    // State
    campaignsRecent: [],
    isLoadingCampaignsRecent: false,
    isLoadingInitialData: true,
    isLoadingNewDate: false,
    hasLoadedInitialData: false,
    timeRange: 'last-7-days',
    loadedDataForBrandId: null,
    selectedDate: {
        startDate: dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
    },
    selectedPlatformForConnect: null,

    platformGraphsData: [],
    overallGraphData: null,
    dashboardTrackedPlatforms: {
        spotify: {
            key: 'spotify_monthly_listeners',
            name: 'Spotify Monthly Listeners',
            platformId: 'spotify',
            platformName: 'Spotify',
            statIdentifier: 'monthly_listeners',
            statLabel: 'Monthly Listeners',
            icon: require('assets/images/platforms/spotify.svg').default,
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.spotify_artist_page?.name || null,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.spotify_artist_page),
            type: 'platforms'
        },
        spotify_followers: {
            key: 'spotify_followers',
            name: 'Spotify Followers',
            platformId: 'spotify',
            platformName: 'Spotify',
            statIdentifier: 'followers',
            statLabel: 'Followers',
            icon: require('assets/images/platforms/spotify.svg').default,
            nameInConnections: 'spotify_artist_page',
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.spotify_artist_page?.name || null,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.spotify_artist_page),
            type: 'platforms'
        },
        youtube: {
            key: 'youtube_subscribers',
            name: 'YouTube Subscribers',
            platformId: 'youtube',
            platformName: 'YouTube',
            statIdentifier: 'subscribers',
            statLabel: 'Subscribers',
            icon: require('assets/images/platforms/youtube.svg').default,
            nameInConnections: 'youtube_channel',
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.youtube_channel?.snippet?.title || null,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.youtube_channel),
            type: 'platforms'
        },
        instagram: {
            key: 'instagram_followers',
            name: 'Instagram Followers',
            platformId: 'instagram',
            platformName: 'Instagram',
            statIdentifier: 'followers',
            statLabel: 'Followers',
            icon: require('assets/images/platforms/instagram.svg').default,
            nameInConnections: 'business_discovery_instagram',
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.business_discovery_instagram?.username,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.business_discovery_instagram),
            type: 'platforms'
        },
        tiktok: {
            key: 'tiktok_followers',
            name: 'TikTok Followers',
            platformId: 'tiktok',
            platformName: 'TikTok',
            statIdentifier: 'followers',
            statLabel: 'Followers',
            icon: require('assets/images/platforms/tiktok.svg').default,
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.tiktok?.username || null,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.tiktok && currentBrand.connections.tiktok.url && currentBrand.connections.tiktok.username),
            type: 'platforms'
        },
        facebook: {
            key: 'facebook_followers',
            name: 'Facebook Page Followers',
            platformId: 'facebook',
            platformName: 'Facebook Page',
            statIdentifier: 'followers',
            statLabel: 'Followers',
            icon: require('assets/images/platforms/facebook.svg').default,
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.facebook_page?.name || null,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.facebook_page && currentBrand.connections.facebook_page.id),
            type: 'platforms'
        },
        soundcloud: {
            key: 'soundcloud_followers',
            name: 'SoundCloud Followers',
            platformId: 'soundcloud',
            platformName: 'SoundCloud',
            statIdentifier: 'followers',
            statLabel: 'Followers',
            icon: require('assets/images/platforms/soundcloud.svg').default,
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.soundcloud_artist_page?.username,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.soundcloud_artist_page),
            type: 'platforms'
        },
        audiomack: {
            key: 'audiomack_followers',
            name: 'Audiomack Followers',
            platformId: 'audiomack',
            platformName: 'Audiomack',
            statIdentifier: 'followers',
            statLabel: 'Followers',
            icon: require('assets/images/platforms/audiomack.svg').default,
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.audiomack?.name || null,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.audiomack),
            type: 'platforms'
        },
        deezer: {
            key: 'deezer_followers',
            name: 'Deezer Fans',
            platformId: 'deezer',
            platformName: 'Deezer',
            statIdentifier: 'followers',
            statLabel: 'Fans',
            icon: require('assets/images/platforms/deezer.svg').default,
            getUsername: (currentBrand: CurrentBrand) => currentBrand.connections?.deezer?.name || null,
            needsConnection: (currentBrand: CurrentBrand) => !(currentBrand.connections?.deezer && currentBrand.connections.deezer.id),
            type: 'platforms'
        },
        // Additional music platforms that only have icons
        // tidal: {
        //     name: 'Tidal',
        //     platformId: 'tidal',
        //     platformName: 'Tidal',
        //     statIdentifier: 'followers',
        //     statLabel: 'Followers',
        //     icon: require('assets/images/platforms/tidal.svg').default,
        //     // needsConnection: () => true,
        //     type: 'platforms'
        // },
        // apple_music: {
        //     name: 'Apple Music',
        //     platformId: 'apple_music',
        //     platformName: 'Apple Music',
        //     statIdentifier: 'followers',
        //     statLabel: 'Followers',
        //     icon: require('assets/images/music-player.svg').default,
        //     needsConnection: () => true,
        //     type: 'platforms'
        // },
        // amazon_music: {
        //     name: 'Amazon Music',
        //     platformId: 'amazon_music',
        //     platformName: 'Amazon Music',
        //     statIdentifier: 'followers',
        //     statLabel: 'Followers',
        //     icon: require('assets/images/music-player.svg').default,
        //     needsConnection: () => true,
        //     type: 'platforms'
        // },
        // Special metrics
        fans: {
            key: 'fans',
            name: 'Fans',
            platformId: 'fans',
            platformName: 'Fans',
            statIdentifier: 'count',
            statLabel: 'Count',
            icon: require('assets/images/platforms/fans.svg').default,
            needsConnection: () => false,
            type: 'symphony'
        },
        forever_saves: {
            key: 'forever_saves',
            name: 'Forever Saves',
            platformId: 'forever-saves',
            platformName: 'Forever Saves',
            statIdentifier: 'count',
            statLabel: 'Count',
            icon: require('assets/images/platforms/forever-saves.svg').default,
            needsConnection: () => false,
            type: 'symphony'
        }
    },

    setCampaignsRecent: (campaignsRecent: CampaignDetails[]) => { set((state) => { state.campaignsRecent = campaignsRecent }) },
    setIsLoadingCampaignsRecent: (isLoadingCampaignsRecent: boolean) => { set((state) => { state.isLoadingCampaignsRecent = isLoadingCampaignsRecent }) },

    setSelectedPlatformForConnect: (selectedPlatformForConnect: SocialConnectType | null) => { set((state) => { state.selectedPlatformForConnect = selectedPlatformForConnect }) },

    // Actions
    isPlatformConnected: (key: DashboardDataType['key']) => {
        const {
            dashboardTrackedPlatforms
        } = get();
        const platform = dashboardTrackedPlatforms[key];
        const currentBrand = useBrand.getState().currentBrand;
        return platform ? !platform.needsConnection(currentBrand!) : false;
    },

    setTimeRange: (timeRange: DATE_OPTIONS) => {
        // Calculate new dates based on timeRange
        const {
            isLoadingInitialData,
            isLoadingNewDate,
            selectedDate,
            getDashboardData
        } = get();

        const endDate = dayjs().format('YYYY-MM-DD');
        const startDate = (() => {
            switch (timeRange) {
                case 'last-30-days':
                    return dayjs().subtract(30, 'days').format('YYYY-MM-DD');
                case 'last-3-months':
                    return dayjs().subtract(90, 'days').format('YYYY-MM-DD');
                case 'custom':
                    // For custom, keep existing selected dates
                    return selectedDate.startDate;
                default: // last-7-days
                    return dayjs().subtract(7, 'days').format('YYYY-MM-DD');
            }
        })();

        // Update both timeRange and selectedDate in a single state update
        set((state) => {
            state.timeRange = timeRange;
            // Only update selectedDate if not custom
            if (timeRange !== 'custom') {
                state.selectedDate = {
                    startDate,
                    endDate
                };
            }
        });

        // Fetch new data if we have valid dates and not already loading
        if (!isLoadingInitialData && !isLoadingNewDate && startDate && endDate) {
            getDashboardData({ startDate, endDate });
        }
    },
    setSelectedDate: (selectedDate: { startDate: string; endDate: string }) => {
        const {
            isLoadingInitialData,
            isLoadingNewDate,
            getDashboardData
        } = get();

        set((state) => {
            state.selectedDate = selectedDate;
        });

        // Fetch new data immediately
        if (!isLoadingInitialData && !isLoadingNewDate && selectedDate.startDate && selectedDate.endDate) {
            getDashboardData({ startDate: selectedDate.startDate, endDate: selectedDate.endDate });
        }
    },

    // Methods
    refreshDashboardData: () => set((state) => {
        try {
            state.isLoadingInitialData = true;
        } catch (e) {
            console.error("Error calling refreshDashboardData", e)
        } finally {
            state.isLoadingInitialData = false;
        }
    }),
    refreshConnections: () => set((state) => {
        try {
            state.isLoadingInitialData = true;
        } catch (e) {
            console.error("Error calling refreshConnections", e)
        } finally {
            state.isLoadingInitialData = false;
        }
    }),
    updateBrandConnections: () => set((state) => {
        try {
            state.isLoadingInitialData = true;
        } catch (e) {
            console.error("Error calling updateBrandConnections", e)
        } finally {
            state.isLoadingInitialData = false;
        }
    }),

    getDashboardData: async ({
        forceRefresh = false,
        startDate,
        endDate
    }: {
        forceRefresh?: boolean;
        startDate?: string;
        endDate?: string;
    }): Promise<any> => {
        const {
            dashboardTrackedPlatforms,
            loadedDataForBrandId,
            selectedDate,
            timeRange
        } = get();
        const currentBrand = useBrand.getState().currentBrand;

        set((state) => {
            state.isLoadingNewDate = true;

            // Simplified condition to set isLoadingInitialData
            if (forceRefresh || !state.hasLoadedInitialData || currentBrand?.id !== loadedDataForBrandId) {
                state.isLoadingInitialData = true;
                state.loadedDataForBrandId = currentBrand?.id || null;
            }
        });

        try {
            // Get fanbase metrics first
            let primaryStartDate = startDate || selectedDate.startDate;
            let primaryEndDate = endDate || selectedDate.endDate;
            const symphonyPlatformMetrics = await getSymphonyPlatformMetrics_api(currentBrand!.id, primaryStartDate, primaryEndDate);

            // Create array of platform data pulls based on PLATFORM_DATA configuration
            // - only platforms with connection are included
            // - only platforms that aren't "symphony" are included (the symphony metrics are handled above)
            const validPlatformsWithConnection = Object.values(dashboardTrackedPlatforms).filter((p: DashboardDataType) => p.type === 'platforms').filter((p: DashboardDataType) => {
                const isConnected = !p.needsConnection(currentBrand!);
                return isConnected
            })

            const externalPlatformMetricsPromiseArray = await Promise.all(
                validPlatformsWithConnection.map(platform => {
                    const { platformId, statIdentifier } = platform;

                    if (!currentBrand) {
                        console.error('currentBrand is undefined');
                        return null;
                    }

                    return getFanbaseData_api({
                        brandId: currentBrand.id,
                        platformId,
                        statIdentifier,
                        startDate: primaryStartDate,
                        endDate: primaryEndDate
                    }).catch(error => {
                        console.error('Error fetching fanbase data:', error);
                        return null;
                    });
                })
            )

            // Wait for all platform data to be fetched
            const externalPlatformMetrics: PlatformDataResponse[] = externalPlatformMetricsPromiseArray.filter((p: PlatformDataResponse | null) => p !== null) as PlatformDataResponse[];

            // Calculate platform graphs data
            const platformGraphsData = calculatePlatformGraphsData({
                symphonyPlatformMetrics,
                externalPlatformMetrics,
                dashboardTrackedPlatforms,
                timeRange,
                selectedDate: { startDate: primaryStartDate, endDate: primaryEndDate }
            });

            // Calculate overall graph data
            const overallGraphData = calculateOverallGraphData({
                dashboardTrackedPlatforms,
                platformData: platformGraphsData
            });

            set((state) => {
                state.platformGraphsData = platformGraphsData;
                state.overallGraphData = overallGraphData;
                state.hasLoadedInitialData = true;
            });

            return {
                fanbaseMetrics: symphonyPlatformMetrics,
                platformData: externalPlatformMetrics
            };
        } catch (error) {
            console.error('Error initializing dashboard data:', error);
            throw error;
        } finally {
            set((state) => {
                state.isLoadingInitialData = false;
                state.isLoadingNewDate = false;
            });
        }
    },

    /** 
     * Fetches and processes data for a specific platform
     * Returns processed chart data with filled gaps and growth percentage
     */
    getDataForSpecificPlatform: async ({ platformId, statIdentifier, startDate, endDate }) => {
        const currentBrand = useBrand.getState().currentBrand;

        if (!currentBrand) {
            throw new Error('No current brand found');
        }

        try {
            const response = await getFanbaseData_api({
                brandId: currentBrand.id,
                platformId,
                statIdentifier,
                startDate,
                endDate
            });

            if (!response?.chartData) {
                return {
                    chartData: [],
                    percentage: 0
                };
            }

            // Convert API response to RawChartData format
            const rawData: RawChartData = {
                chartData: response.chartData,
                growthData: {
                    currentGrowthValue: parseInt(response.chartData[response.chartData.length - 1]?.fanbase_count) || 0,
                    percentage: response.growthData?.percentage || 0
                }
            };

            // Process data with gap filling
            const processedData = fillInDataGaps(
                rawData,
                'custom', // Use custom since we're passing specific dates
                { startDate, endDate }
            );

            return {
                chartData: processedData.chartData,
                percentage: processedData.growthData.percentage
            };

        } catch (error) {
            console.error('Error fetching platform data:', error);
            throw error;
        }
    },

    getCampaignsRecent: async ({
        brandId,
        isAnalytics
    }: {
        brandId: number,
        isAnalytics?: boolean
    }) => {
        set({ isLoadingCampaignsRecent: true })
        try {
            let campaignFilterLocal = { ...CAMPAIGN_FILTER_DEFAULT }
            campaignFilterLocal.campaignStatuses = [CampaignStatus.ACTIVE, CampaignStatus.FINISHED, CampaignStatus.IN_REVIEW]

            if (isAnalytics) {
                campaignFilterLocal.isAnalytics = true
            }

            const campaignsRecent = await getCampaignsByBrandId_api({
                brandId: Number(brandId),
                params: campaignFilterLocal,
            })
            set({ campaignsRecent: campaignsRecent || [] })
            return campaignsRecent
        } catch (error) {
            console.error('Error fetching campaigns:', error)
            return []
        } finally {
            set({ isLoadingCampaignsRecent: false })
        }
    },
})))

const calculatePlatformGraphsData = ({
    symphonyPlatformMetrics,
    externalPlatformMetrics,
    dashboardTrackedPlatforms,
    timeRange,
    selectedDate
}: {
    symphonyPlatformMetrics: FanbaseMetricsType | null,
    externalPlatformMetrics: PlatformDataResponse[] | null,
    dashboardTrackedPlatforms: Record<string, DashboardDataType>,
    timeRange: DATE_OPTIONS,
    selectedDate: { startDate: string; endDate: string }
}): GraphPresentation[] => {
    const platformGraphsData: GraphPresentation[] = [];

    // Add Symphony platform metrics if available
    if (symphonyPlatformMetrics?.dailyMetrics) {
        // Process total fans data
        const totalFansRawData: RawChartData = {
            chartData: Object.entries(symphonyPlatformMetrics.dailyMetrics).map(([date, data]) => ({
                datetime: date,
                fanbase_count: data.totalFans || 0
            })),
            growthData: {
                currentGrowthValue: symphonyPlatformMetrics.totalFanbase || 0,
                percentage: parseFloat(symphonyPlatformMetrics.fanbaseGrowth || '0')
            }
        };

        const processedFansData = fillInDataGaps(totalFansRawData, timeRange, selectedDate);

        // Add total fans data
        const totalFansData: GraphPresentation = {
            key: 'fans',
            platformId: 'fans',
            platformName: 'Symphony',
            statIdentifier: 'count',
            statName: 'Connected Fans',
            isConnected: true,
            chartData: processedFansData.chartData,
            imageAsset: dashboardTrackedPlatforms.fans.icon,
            currentValue: symphonyPlatformMetrics.totalFanbase || 0,
            shown: true,
            changeValue: processedFansData.growthData.percentage
        };

        // Process forever saves data
        const foreverSavesRawData: RawChartData = {
            chartData: Object.entries(symphonyPlatformMetrics.dailyMetrics).map(([date, data]) => ({
                datetime: date,
                fanbase_count: data.foreverSaves || 0
            })),
            growthData: {
                currentGrowthValue: symphonyPlatformMetrics.foreverPresaveFans || 0,
                percentage: parseFloat(symphonyPlatformMetrics.foreverPresaveFansGrowth || '0')
            }
        };

        const processedSavesData = fillInDataGaps(foreverSavesRawData, timeRange, selectedDate);

        // Add forever saves data
        const foreverSavesData: GraphPresentation = {
            key: 'forever_saves',
            platformId: 'forever-saves',
            platformName: 'Symphony',
            statIdentifier: 'count',
            statName: 'Forever Saves',
            isConnected: true,
            chartData: processedSavesData.chartData,
            imageAsset: dashboardTrackedPlatforms['forever_saves'].icon,
            currentValue: symphonyPlatformMetrics.foreverPresaveFans || 0,
            shown: true,
            changeValue: processedSavesData.growthData.percentage
        };

        platformGraphsData.push(totalFansData, foreverSavesData);
    }

    // Process all platforms from dashboardTrackedPlatforms
    Object.entries(dashboardTrackedPlatforms).forEach(([key, platformConfig]) => {
        // Skip Symphony metrics as they're handled separately
        if (platformConfig.type === 'symphony') {
            return;
        }

        const currentBrand = useBrand.getState().currentBrand!;
        const isConnected = !platformConfig.needsConnection(currentBrand);

        // Find platform data if it exists
        const platformData = externalPlatformMetrics?.find(
            p => p.platform === platformConfig.platformId && p.stat === platformConfig.statIdentifier
        );

        if (isConnected && platformData) {
            // Process connected platform with data
            const mostRecentValue = platformData.chartData?.[platformData.chartData.length - 1];
            const rawData: RawChartData = {
                chartData: platformData.chartData || [],
                growthData: {
                    currentGrowthValue: parseInt(mostRecentValue?.fanbase_count) || 0,
                    percentage: platformData.growthData?.percentage || 0
                }
            };

            const processedData = fillInDataGaps(rawData, timeRange, selectedDate);

            const graphPresentation: GraphPresentation = {
                key: platformConfig.key,
                platformId: platformConfig.platformId,
                platformName: platformConfig.platformName,
                statIdentifier: platformConfig.statIdentifier,
                statName: platformConfig.statLabel,
                isConnected: true,
                chartData: processedData.chartData,
                imageAsset: platformConfig.icon,
                currentValue: Number(processedData.chartData[processedData.chartData.length - 1]?.value || 0),
                shown: true,
                changeValue: processedData.growthData.percentage,
                decommissionMessage: platformConfig.decommissionMessage
            };

            platformGraphsData.push(graphPresentation);
        } else {
            // Add disconnected or empty platform
            const emptyGraphPresentation: GraphPresentation = {
                key: platformConfig.key,
                platformId: platformConfig.platformId,
                platformName: platformConfig.platformName,
                statIdentifier: platformConfig.statIdentifier,
                statName: platformConfig.statLabel,
                isConnected: isConnected,
                chartData: [],
                imageAsset: platformConfig.icon,
                currentValue: 0,
                shown: true,
                changeValue: 0,
                decommissionMessage: platformConfig.decommissionMessage
            };

            platformGraphsData.push(emptyGraphPresentation);
        }
    });

    // Sort platforms by connection status
    return platformGraphsData.sort((a, b) => {
        if (a.isConnected === b.isConnected) return 0;
        return a.isConnected ? -1 : 1;
    });
}

/** 
 * Aggregates data from all platforms to create the overall fanbase graph data
 * @returns Object containing chart data and growth metrics for overall fanbase
 */
const calculateOverallGraphData = ({
    dashboardTrackedPlatforms,
    platformData
}: {
    dashboardTrackedPlatforms: Record<string, DashboardDataType>,
    platformData: GraphPresentation[]
}): OverallGraphData => {
    // Create a map of date -> total value
    const dateMap = new Map<string, number>();

    // For each platform metric, add its values to the total if not hidden
    platformData.forEach(platform => {
        // Get the platform config using the exact key
        const platformConfig = dashboardTrackedPlatforms[platform.key];

        // Skip if platform should be hidden from overall
        if (platformConfig?.hideFromOverall) {
            return;
        }

        if (platform.chartData) {
            platform.chartData.forEach(dataPoint => {
                const currentValue = dateMap.get(dataPoint.date) || 0;
                dateMap.set(dataPoint.date, currentValue + dataPoint.value);
            });
        }
    });

    // Convert to sorted array format
    const sortedDates = Array.from(dateMap.entries())
        .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
        .map(([date, value]) => ({
            date,
            value
        }));

    // Calculate growth percentage
    const firstValue = sortedDates[0]?.value || 0;
    const lastValue = sortedDates[sortedDates.length - 1]?.value || 0;
    const growthPercentage = firstValue > 0 ?
        ((lastValue - firstValue) / firstValue) * 100 : 0;

    return {
        chartData: sortedDates,
        percentage: growthPercentage,
        labels: sortedDates.map(item => item.date)
    };
}

/**
 * Fills gaps in chart data by interpolating values from adjacent days or using nearest valid value
 * @param chartData Raw chart data with possible gaps
 * @param timeRange Selected time range
 * @param selectedDate Selected date range
 * @returns Processed chart data with gaps filled
 */
const fillInDataGaps = (
    chartData: RawChartData,
    timeRange: DATE_OPTIONS,
    selectedDate: { startDate: string; endDate: string }
): ProcessedChartData => {
    // Calculate date range based on timeRange first
    const endDateObj = dayjs();
    const startDateObj = (() => {
        switch (timeRange) {
            case 'last-30-days':
                return endDateObj.subtract(30, 'days');
            case 'last-3-months':
                return endDateObj.subtract(90, 'days');
            case 'custom':
                return dayjs(selectedDate.startDate);
            default: // last-7-days
                return endDateObj.subtract(7, 'days');
        }
    })();

    const finalEndDateObj = timeRange === 'custom' ?
        dayjs(selectedDate.endDate) :
        endDateObj;

    if (!startDateObj.isValid() || !finalEndDateObj.isValid()) {
        console.error('Invalid date range calculation');
        return {
            chartData: [],
            growthData: { currentGrowthValue: 0, percentage: 0 }
        };
    }

    // Calculate total days based on date range
    const totalDays = finalEndDateObj.diff(startDateObj, 'day') + 1;

    // Return empty data if no chart data available
    if (!chartData.chartData?.length) {
        return {
            chartData: [],
            growthData: { currentGrowthValue: 0, percentage: 0 }
        };
    }

    /** Helper to find valid value within range */
    const findValidValue = (data: ChartDataPoint[], index: number, searchRange: number = 6): number => {
        // Forward search
        for (let i = 1; i <= searchRange && index + i < data.length; i++) {
            if (data[index + i].value > 0) {
                return data[index + i].value;
            }
        }
        // Backward search
        for (let i = 1; i <= searchRange && index - i >= 0; i++) {
            if (data[index - i].value > 0) {
                return data[index - i].value;
            }
        }
        return 0;
    };

    // Generate initial data points with explicit date formatting
    const localChartData: ChartDataPoint[] = Array.from({ length: totalDays }, (_, i) => {
        const date = startDateObj.add(i, 'day');
        const dateStr = date.format('YYYY-MM-DD');

        const dateData = chartData.chartData.find(d =>
            dayjs(d.datetime).format('YYYY-MM-DD') === dateStr
        );

        const value = dateData ? parseInt(String(dateData.fanbase_count)) : 0;
        return {
            date: dateStr,
            value: isNaN(value) ? 0 : value
        };
    });

    // First pass: Fill gaps using adjacent values or interpolation
    for (let i = 0; i < localChartData.length; i++) {
        if (localChartData[i].value > 0) continue;

        // Try to get values from adjacent days
        const prevValue = i > 0 ? localChartData[i - 1].value : 0;
        const nextValue = i < localChartData.length - 1 ? localChartData[i + 1].value : 0;

        if (prevValue > 0 && nextValue > 0) {
            // If both adjacent values exist, use average
            localChartData[i].value = Math.round((prevValue + nextValue) / 2);
        } else {
            // Otherwise search for nearest valid value
            const validValue = findValidValue(localChartData, i);
            if (validValue > 0) {
                localChartData[i].value = validValue;
            }
        }
    }

    // Second pass: Fill any remaining gaps with first found value
    const firstFoundValue = localChartData.find(d => d.value > 0)?.value || 0;
    if (firstFoundValue > 0) {
        localChartData.forEach(data => {
            if (data.value === 0) {
                data.value = firstFoundValue;
            }
        });
    }

    // Calculate growth percentage
    const firstValue = localChartData[0].value;
    const lastValue = localChartData[localChartData.length - 1].value;
    const growthPercentage = firstValue > 0 ?
        ((lastValue - firstValue) / firstValue) * 100 : 0;

    return {
        chartData: localChartData,
        growthData: {
            currentGrowthValue: chartData.growthData.currentGrowthValue,
            percentage: growthPercentage
        }
    };
};